import { useState, useEffect } from 'react'
import {
  Card, Table, Button, Modal, Form, Badge, Spinner, Alert, Tab, Tabs, Row, Col
} from 'react-bootstrap'
import { toast } from 'react-toastify'
import api from '../services/api'
import { useAuthStore } from '../store/authStore'

interface Backup {
  id: string
  name: string
  description: string | null
  filename: string
  file_size: string | null
  includes_extensions: boolean
  includes_trunks: boolean
  includes_routing: boolean
  includes_ivr: boolean
  includes_recordings: boolean
  includes_users: boolean
  is_automatic: boolean
  created_at: string
  user_name: string | null
  tenant_name: string | null
}

interface ConfigChange {
  id: string
  entity_type: string
  entity_id: string
  entity_name: string | null
  change_type: 'create' | 'update' | 'delete'
  description: string | null
  restorable: boolean
  restored_at: string | null
  created_at: string
  user_name: string | null
  tenant_name: string | null
  data_before: string | null
  data_after: string | null
}

interface EntityType {
  value: string
  label: string
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  extension: 'Nebenstelle',
  trunk: 'Trunk',
  ivr_menu: 'IVR-Menü',
  time_condition: 'Zeitbedingung',
  inbound_route: 'Eingehende Route',
  outbound_route: 'Ausgehende Route',
  feature_code: 'Feature-Code',
  queue: 'Warteschlange',
  conference: 'Konferenz',
  recording: 'Aufnahme',
  phonebook: 'Telefonbuch',
  global_settings: 'Globale Einstellungen',
  ami_user: 'AMI-Benutzer',
  user: 'Benutzer',
  tenant: 'Mandant'
}

const CHANGE_TYPE_LABELS: Record<string, { label: string; variant: string }> = {
  create: { label: 'Erstellt', variant: 'success' },
  update: { label: 'Geändert', variant: 'warning' },
  delete: { label: 'Gelöscht', variant: 'danger' }
}

export default function Backup() {
  const user = useAuthStore(state => state.user)
  const isSuperAdmin = user?.role === 'super_admin'

  // Backup state
  const [backups, setBackups] = useState<Backup[]>([])
  const [backupsLoading, setBackupsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newBackup, setNewBackup] = useState({
    name: '',
    description: '',
    includes_extensions: true,
    includes_trunks: true,
    includes_routing: true,
    includes_ivr: true,
    includes_recordings: false,
    includes_users: true
  })

  // Config changes state
  const [changes, setChanges] = useState<ConfigChange[]>([])
  const [changesLoading, setChangesLoading] = useState(true)
  const [changesTotal, setChangesTotal] = useState(0)
  const [entityTypes, setEntityTypes] = useState<EntityType[]>([])
  const [filterEntityType, setFilterEntityType] = useState<string>('')
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [selectedChange, setSelectedChange] = useState<ConfigChange | null>(null)
  const [restoring, setRestoring] = useState(false)

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [backupToDelete, setBackupToDelete] = useState<Backup | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    loadBackups()
    loadChanges()
    loadEntityTypes()
  }, [filterEntityType])

  const loadBackups = async () => {
    setBackupsLoading(true)
    try {
      const res = await api.get('/backups')
      setBackups(res.data.items || [])
    } catch (error) {
      console.error('Failed to load backups:', error)
      toast.error('Fehler beim Laden der Backups')
    } finally {
      setBackupsLoading(false)
    }
  }

  const loadChanges = async () => {
    setChangesLoading(true)
    try {
      const params = new URLSearchParams()
      params.append('limit', '100')
      if (filterEntityType) {
        params.append('entity_type', filterEntityType)
      }
      const res = await api.get(`/backups/changes?${params.toString()}`)
      setChanges(res.data.items || [])
      setChangesTotal(res.data.total || 0)
    } catch (error) {
      console.error('Failed to load changes:', error)
    } finally {
      setChangesLoading(false)
    }
  }

  const loadEntityTypes = async () => {
    try {
      const res = await api.get('/backups/entity-types/list')
      setEntityTypes(res.data.types || [])
    } catch (error) {
      console.error('Failed to load entity types:', error)
    }
  }

  const handleCreateBackup = async () => {
    if (!newBackup.name.trim()) {
      toast.error('Bitte geben Sie einen Namen ein')
      return
    }

    setCreating(true)
    try {
      await api.post('/backups', newBackup)
      toast.success('Backup erfolgreich erstellt')
      setShowCreateModal(false)
      setNewBackup({
        name: '',
        description: '',
        includes_extensions: true,
        includes_trunks: true,
        includes_routing: true,
        includes_ivr: true,
        includes_recordings: false,
        includes_users: true
      })
      loadBackups()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Backups')
    } finally {
      setCreating(false)
    }
  }

  const handleDownloadBackup = async (backup: Backup) => {
    try {
      const res = await api.get(`/backups/${backup.id}/download`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', backup.filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast.error('Fehler beim Download')
    }
  }

  const handleDeleteBackup = async () => {
    if (!backupToDelete) return

    setDeleting(true)
    try {
      await api.delete(`/backups/${backupToDelete.id}`)
      toast.success('Backup gelöscht')
      setShowDeleteModal(false)
      setBackupToDelete(null)
      loadBackups()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen')
    } finally {
      setDeleting(false)
    }
  }

  const openRestoreModal = (change: ConfigChange) => {
    setSelectedChange(change)
    setShowRestoreModal(true)
  }

  const handleRestore = async () => {
    if (!selectedChange) return

    setRestoring(true)
    try {
      const res = await api.post(`/backups/changes/${selectedChange.id}/restore`, {
        confirm: true
      })
      if (res.data.success) {
        toast.success(res.data.message || 'Wiederherstellung erfolgreich')
        setShowRestoreModal(false)
        setSelectedChange(null)
        loadChanges()
      } else {
        toast.error(res.data.message || 'Wiederherstellung fehlgeschlagen')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Wiederherstellung')
    } finally {
      setRestoring(false)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getChangeDescription = (change: ConfigChange) => {
    const entityLabel = ENTITY_TYPE_LABELS[change.entity_type] || change.entity_type
    const name = change.entity_name || change.entity_id

    if (change.change_type === 'create') {
      return `${entityLabel} "${name}" erstellt`
    } else if (change.change_type === 'update') {
      return `${entityLabel} "${name}" geändert`
    } else {
      return `${entityLabel} "${name}" gelöscht`
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <i className="bi bi-cloud-arrow-down me-2"></i>
          Backup & Wiederherstellung
        </h4>
      </div>

      <Tabs defaultActiveKey="backups" className="mb-4">
        {/* Backups Tab */}
        <Tab eventKey="backups" title={<><i className="bi bi-archive me-2"></i>Backups</>}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-archive me-2"></i>
                Gespeicherte Backups
              </span>
              <Button variant="success" size="sm" onClick={() => setShowCreateModal(true)}>
                <i className="bi bi-plus-lg me-1"></i> Backup erstellen
              </Button>
            </Card.Header>
            <Card.Body>
              {backupsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : backups.length === 0 ? (
                <Alert variant="info">
                  <i className="bi bi-info-circle me-2"></i>
                  Noch keine Backups vorhanden. Erstellen Sie Ihr erstes Backup!
                </Alert>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Erstellt am</th>
                      <th>Erstellt von</th>
                      <th>Größe</th>
                      <th>Inhalt</th>
                      {isSuperAdmin && <th>Mandant</th>}
                      <th style={{ width: '120px' }}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backups.map(backup => (
                      <tr key={backup.id}>
                        <td>
                          <strong>{backup.name}</strong>
                          {backup.description && (
                            <div className="text-muted small">{backup.description}</div>
                          )}
                        </td>
                        <td>{formatDate(backup.created_at)}</td>
                        <td>{backup.user_name || '-'}</td>
                        <td>{backup.file_size || '-'}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {backup.includes_extensions && <Badge bg="primary">Nebenstellen</Badge>}
                            {backup.includes_trunks && <Badge bg="secondary">Trunks</Badge>}
                            {backup.includes_routing && <Badge bg="info">Routing</Badge>}
                            {backup.includes_ivr && <Badge bg="warning">IVR</Badge>}
                            {backup.includes_users && <Badge bg="dark">Benutzer</Badge>}
                          </div>
                        </td>
                        {isSuperAdmin && (
                          <td>
                            {backup.tenant_name ? (
                              <Badge bg="secondary">{backup.tenant_name}</Badge>
                            ) : (
                              <Badge bg="danger">System</Badge>
                            )}
                          </td>
                        )}
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleDownloadBackup(backup)}
                            title="Herunterladen"
                          >
                            <i className="bi bi-download"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              setBackupToDelete(backup)
                              setShowDeleteModal(true)
                            }}
                            title="Löschen"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Config Changes Tab */}
        <Tab eventKey="changes" title={<><i className="bi bi-clock-history me-2"></i>Änderungshistorie ({changesTotal})</>}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-clock-history me-2"></i>
                Letzte 100 Konfigurationsänderungen
              </span>
              <Form.Select
                size="sm"
                style={{ width: '200px' }}
                value={filterEntityType}
                onChange={(e) => setFilterEntityType(e.target.value)}
              >
                <option value="">Alle Typen</option>
                {entityTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </Form.Select>
            </Card.Header>
            <Card.Body>
              {changesLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : changes.length === 0 ? (
                <Alert variant="info">
                  <i className="bi bi-info-circle me-2"></i>
                  Keine Änderungen gefunden.
                </Alert>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th style={{ width: '170px' }}>Zeitpunkt</th>
                      <th>Änderung</th>
                      <th style={{ width: '100px' }}>Typ</th>
                      <th style={{ width: '120px' }}>Benutzer</th>
                      {isSuperAdmin && <th style={{ width: '120px' }}>Mandant</th>}
                      <th style={{ width: '130px' }}>Status</th>
                      <th style={{ width: '80px' }}>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changes.map(change => {
                      const changeInfo = CHANGE_TYPE_LABELS[change.change_type]
                      return (
                        <tr key={change.id}>
                          <td className="text-nowrap">{formatDate(change.created_at)}</td>
                          <td>
                            {getChangeDescription(change)}
                            {change.description && (
                              <div className="text-muted small">{change.description}</div>
                            )}
                          </td>
                          <td>
                            <Badge bg={changeInfo?.variant || 'secondary'}>
                              {changeInfo?.label || change.change_type}
                            </Badge>
                          </td>
                          <td>{change.user_name || '-'}</td>
                          {isSuperAdmin && (
                            <td>
                              {change.tenant_name ? (
                                <Badge bg="secondary">{change.tenant_name}</Badge>
                              ) : (
                                <Badge bg="danger">System</Badge>
                              )}
                            </td>
                          )}
                          <td>
                            {change.restored_at ? (
                              <Badge bg="info">
                                <i className="bi bi-check-circle me-1"></i>
                                Wiederhergestellt
                              </Badge>
                            ) : change.restorable ? (
                              <Badge bg="success">
                                <i className="bi bi-arrow-counterclockwise me-1"></i>
                                Wiederherstellbar
                              </Badge>
                            ) : (
                              <Badge bg="secondary">
                                <i className="bi bi-x-circle me-1"></i>
                                Nicht möglich
                              </Badge>
                            )}
                          </td>
                          <td>
                            {change.restorable && !change.restored_at && (
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => openRestoreModal(change)}
                                title="Wiederherstellen"
                              >
                                <i className="bi bi-arrow-counterclockwise"></i>
                              </Button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Create Backup Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-plus-circle me-2"></i>
            Neues Backup erstellen
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="z.B. Vor dem Update"
              value={newBackup.name}
              onChange={(e) => setNewBackup({ ...newBackup, name: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Beschreibung</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Optionale Beschreibung..."
              value={newBackup.description}
              onChange={(e) => setNewBackup({ ...newBackup, description: e.target.value })}
            />
          </Form.Group>

          <h6 className="mb-3">Was soll gesichert werden?</h6>
          <Row>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="includes_extensions"
                label="Nebenstellen"
                checked={newBackup.includes_extensions}
                onChange={(e) => setNewBackup({ ...newBackup, includes_extensions: e.target.checked })}
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="includes_trunks"
                label="Trunks"
                checked={newBackup.includes_trunks}
                onChange={(e) => setNewBackup({ ...newBackup, includes_trunks: e.target.checked })}
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="includes_routing"
                label="Routing (Inbound/Outbound)"
                checked={newBackup.includes_routing}
                onChange={(e) => setNewBackup({ ...newBackup, includes_routing: e.target.checked })}
                className="mb-2"
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="includes_ivr"
                label="IVR & Zeitbedingungen"
                checked={newBackup.includes_ivr}
                onChange={(e) => setNewBackup({ ...newBackup, includes_ivr: e.target.checked })}
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="includes_users"
                label="Benutzer & AMI-Benutzer"
                checked={newBackup.includes_users}
                onChange={(e) => setNewBackup({ ...newBackup, includes_users: e.target.checked })}
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="includes_recordings"
                label="Audio-Aufnahmen (kann groß sein)"
                checked={newBackup.includes_recordings}
                onChange={(e) => setNewBackup({ ...newBackup, includes_recordings: e.target.checked })}
                className="mb-2"
              />
            </Col>
          </Row>

          <Alert variant="info" className="mt-3 mb-0">
            <i className="bi bi-info-circle me-2"></i>
            {isSuperAdmin ? (
              <>Das Backup enthält <strong>alle Mandanten</strong> und ihre Daten.</>
            ) : (
              <>Das Backup enthält nur die Daten <strong>Ihres Mandanten</strong>.</>
            )}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Abbrechen
          </Button>
          <Button variant="success" onClick={handleCreateBackup} disabled={creating || !newBackup.name.trim()}>
            {creating ? (
              <>
                <Spinner size="sm" className="me-2" />
                Erstelle...
              </>
            ) : (
              <>
                <i className="bi bi-cloud-arrow-up me-2"></i>
                Backup erstellen
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Backup Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle text-danger me-2"></i>
            Backup löschen
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Möchten Sie dieses Backup wirklich löschen?</p>
          {backupToDelete && (
            <Alert variant="warning">
              <strong>{backupToDelete.name}</strong>
              <br />
              <small className="text-muted">Erstellt am {formatDate(backupToDelete.created_at)}</small>
            </Alert>
          )}
          <p className="text-danger mb-0">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Diese Aktion kann nicht rückgängig gemacht werden!
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Abbrechen
          </Button>
          <Button variant="danger" onClick={handleDeleteBackup} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Lösche...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-2"></i>
                Löschen
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Restore Change Modal */}
      <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-arrow-counterclockwise text-warning me-2"></i>
            Änderung wiederherstellen
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedChange && (
            <>
              <p>Möchten Sie diese Änderung rückgängig machen?</p>
              <Alert variant="info">
                <strong>{getChangeDescription(selectedChange)}</strong>
                <br />
                <small className="text-muted">
                  Am {formatDate(selectedChange.created_at)} von {selectedChange.user_name || 'Unbekannt'}
                </small>
              </Alert>

              {selectedChange.change_type === 'delete' && (
                <p>
                  <i className="bi bi-arrow-right me-2"></i>
                  Das gelöschte Element wird wiederhergestellt.
                </p>
              )}
              {selectedChange.change_type === 'update' && (
                <p>
                  <i className="bi bi-arrow-right me-2"></i>
                  Das Element wird auf den vorherigen Zustand zurückgesetzt.
                </p>
              )}
              {selectedChange.change_type === 'create' && (
                <p>
                  <i className="bi bi-arrow-right me-2"></i>
                  Das neu erstellte Element wird gelöscht.
                </p>
              )}

              <Alert variant="warning" className="mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Hinweis:</strong> Nach der Wiederherstellung muss möglicherweise die Asterisk-Konfiguration neu geladen werden.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRestoreModal(false)}>
            Abbrechen
          </Button>
          <Button variant="warning" onClick={handleRestore} disabled={restoring}>
            {restoring ? (
              <>
                <Spinner size="sm" className="me-2" />
                Wiederherstellen...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-counterclockwise me-2"></i>
                Wiederherstellen
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
